import React, { useState, useEffect } from "react";
import { toast } from 'react-hot-toast';
import { createMember, updateMember } from './api';
import { useNavigate, useLocation } from "react-router-dom";
import { useLanguage } from './LanguageContext';
import Layout from './Layout';

const CreateMember = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const location = useLocation();
  const editMode = location.state?.editMode;
  const memberData = location.state?.memberData;

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [subscription, setSubscription] = useState("Bronze");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    if (editMode && memberData) {
      setName(memberData.name || "");
      setEmail(memberData.email || "");
      setPhone(memberData.phone || "");
      setSubscription(memberData.subscriptionType || "Bronze");

      // Helper to format date string to YYYY-MM-DD
      const formatDate = (dateString) => {
        if (!dateString) return "";
        try {
          return new Date(dateString).toISOString().split('T')[0];
        } catch (e) {
          return "";
        }
      };

      setStartDate(formatDate(memberData.startDate));
      setEndDate(formatDate(memberData.endDate));
    }
  }, [editMode, memberData]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    let formattedStartDate = null;
    let formattedEndDate = null;

    try {
      if (startDate) formattedStartDate = new Date(startDate).toISOString();
      if (endDate) formattedEndDate = new Date(endDate).toISOString();
    } catch (e) {
      toast.error("Invalid date format. Please check your dates.");
      return;
    }

    // Map frontend fields to backend Pydantic schema (AdminUserCreate)
    const formData = {
      name,
      email,
      phone,
      membership_type: subscription, // Frontend: subscription -> Backend: membership_type
      start_date: formattedStartDate, // Frontend: startDate -> Backend: start_date
      end_date: formattedEndDate,     // Frontend: endDate -> Backend: end_date
    };

    if (editMode) {
      console.log("Editing Member:", formData);
      try {
        if (!memberData || !memberData.id) {
          throw new Error("Missing member ID for update");
        }
        await updateMember(memberData.id, formData);
        toast.success("Member updated successfully!");
        navigate('/Home');
      } catch (error) {
        console.error("Failed to update member:", error);
        toast.error("Failed to update member");
      }
    } else {
      try {
        await createMember(formData);
        toast.success("Member created successfully!");
        navigate('/Home');
      } catch (error) {
        console.error("Failed to create member:", error);
        toast.error("Failed to create member");
      }
    }
  };

  const handleResetPassword = async () => {
    if (!memberData || !memberData.id) return;

    // Validate dates before sending
    let formattedStartDate = null;
    let formattedEndDate = null;
    try {
      if (startDate) formattedStartDate = new Date(startDate).toISOString();
      if (endDate) formattedEndDate = new Date(endDate).toISOString();
    } catch (e) {
      toast.error("Invalid date format. Please check your dates.");
      return;
    }

    const formData = {
      name,
      email,
      phone,
      membership_type: subscription,
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      is_first_time: true,
      password_reset: true
    };

    console.log("Resetting Password for Member:", formData);
    try {
      await updateMember(memberData.id, formData);
      toast.success("Password reset successfully!");
      navigate('/Home');
    } catch (error) {
      console.error("Failed to reset password:", error);
      toast.error("Failed to reset password");
    }
  };

  return (
    <Layout mainClassName="flex justify-center p-10 pt-20">
      <div className="w-full max-w-md flex flex-col items-center">
        <h1 className="text-3xl font-extrabold text-slate-800 mb-8">
          {editMode ? t('editTitle') : t('createTitle')}
        </h1>



        {/* Form */}
        <form className="w-full flex flex-col items-center gap-5" id="member-form" onSubmit={handleSubmit}>
          {/* Name */}
          <div className="w-full bg-gradient-to-b from-white to-[#F0F2F5] rounded-full shadow-[0_8px_16px_-4px_rgb(0,0,0)] hover:shadow-[0_10px_20px_-4px_rgb(0,0,0)] transition-all">
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('namePlaceholder')}
              className="w-full px-6 py-4 rounded-full bg-transparent text-sm text-slate-900 placeholder-gray-400 focus:outline-none"
            />
          </div>

          {/* Email */}
          <div className="w-full bg-gradient-to-b from-white to-[#F0F2F5] rounded-full shadow-[0_8px_16px_-4px_rgb(0,0,0)] hover:shadow-[0_10px_20px_-4px_rgb(0,0,0)] transition-all">
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('emailPlaceholder')}
              className="w-full px-6 py-4 rounded-full bg-transparent text-sm text-slate-900 placeholder-gray-400 focus:outline-none"
            />
          </div>

          {/* Phone */}
          <div className="w-full bg-gradient-to-b from-white to-[#F0F2F5] rounded-full shadow-[0_8px_16px_-4px_rgb(0,0,0)] hover:shadow-[0_10px_20px_-4px_rgb(0,0,0)] transition-all">
            <input
              type="tel"
              id="phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder={t('phonePlaceholder')}
              className="w-full px-6 py-4 rounded-full bg-transparent text-sm text-slate-900 placeholder-gray-400 focus:outline-none"
            />
          </div>

          {/* Subscription */}
          <div className="relative w-full bg-gradient-to-b from-white to-[#F0F2F5] rounded-full shadow-[0_8px_16px_-4px_rgb(0,0,0)] hover:shadow-[0_10px_20px_-4px_rgb(0,0,0)] transition-all">
            <select
              id="subscription"
              className="w-full px-6 py-4 rounded-full bg-transparent text-sm text-slate-900 focus:outline-none appearance-none cursor-pointer"
              value={subscription}
              onChange={(e) => setSubscription(e.target.value)}
            >
              <option value="Bronze">{t('subscription_bronze')}</option>
              <option value="Silver">{t('subscription_silver')}</option>
              <option value="Gold">{t('subscription_gold')}</option>
              <option value="Platinum">{t('subscription_platinum')}</option>
            </select>
            <div className="absolute right-6 top-1/2 -translate-y-1/2 pointer-events-none">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-gray-500">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </div>
          </div>

          {/* Dates */}
          <div className="w-full flex gap-4">
            <div className="w-full bg-gradient-to-b from-white to-[#F0F2F5] rounded-full shadow-[0_8px_16px_-4px_rgb(0,0,0)] hover:shadow-[0_10px_20px_-4px_rgb(0,0,0)] transition-all">
              <input
                type="date"
                id="start-date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                placeholder={t('startPlaceholder')}
                className="w-full px-6 py-4 rounded-full bg-transparent text-sm text-slate-900 placeholder-gray-400 focus:outline-none"
              />
            </div>
            <div className="w-full bg-gradient-to-b from-white to-[#F0F2F5] rounded-full shadow-[0_8px_16px_-4px_rgb(0,0,0)] hover:shadow-[0_10px_20px_-4px_rgb(0,0,0)] transition-all">
              <input
                type="date"
                id="end-date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                placeholder={t('endPlaceholder')}
                className="w-full px-6 py-4 rounded-full bg-transparent text-sm text-slate-900 placeholder-gray-400 focus:outline-none"
              />
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            className="w-full py-4 bg-gradient-to-r from-[#02131bff] to-[#364f58ff] text-white font-bold rounded-full shadow-[0_8px_16px_-4px_rgb(0,0,0)] hover:shadow-[0_10px_20px_-4px_rgb(0,0,0)] hover:-translate-y-1 transition-all"
          >
            {editMode ? t('saveButton') : t('createButton')}
          </button>

          {editMode && (
            <button
              type="button"
              onClick={handleResetPassword}
              className="w-full py-4 bg-orange-500 text-white font-bold rounded-full shadow-[0_8px_16px_-4px_rgb(0,0,0)] hover:shadow-[0_10px_20px_-4px_rgb(0,0,0)] hover:-translate-y-1 transition-all"
            >
              Reset Password
            </button>
          )}
        </form>
      </div>
    </Layout>
  );
};

export default CreateMember;
